/* ========================================================================
   COLDEST COLLECTION PAGE CRO — ALL PHASES JS
   Target: /collections/* pages
   Deployed: March 2026
   
   NOTE: Boost Commerce Product Filter & Search replaces the native
   Shopify collection template with its own rendering. The original
   [data-section-type="collection-template"] container collapses to 0×0.
   All DOM insertions must target VISIBLE containers:
   - Hero banner is in its own visible section above Boost
   - Boost renders into #boost-pfs-filter-products (visible)
   - We insert trust bar AFTER the hero section
   - We insert media bar / comparison AFTER Boost's product container
   ======================================================================== */

(function() {
  'use strict';

  // Only run on collection pages
  if (!window.location.pathname.startsWith('/collections/')) return;

  // Boost Commerce is no longer installed (theme.liquid's boost-pfs slots are
  // empty and no page renders .boost-sd__* markup). The Boost-only waiters
  // below polled for ~15s on every collection page, the Meta ad lander
  // included, for nothing. They now run only if Boost is ever back.
  var HAS_BOOST = !!document.querySelector('script[src*="boost-sd"],script[src*="boost-pfs"],script[src*="bc-sf-filter"]') ||
    !!(window.boostSDAppConfig || window.boostSD || window.BoostPFS || window.boostPFSAppConfig);

  // Signature-event pages (Mega Live framework, template-suffix mega-live)
  // are a complete self-contained experience — never inject collection-CRO
  // blocks there. Without this bail, the crate VIDEO on that page becomes the
  // media-bar/comparison-table injection anchor ("insert before the video
  // section") and both blocks land ABOVE the entire Mega Live section,
  // shoving it below the fold.
  if ((document.body.className || '').indexOf('template-suffix-mega-live') !== -1
      || document.querySelector('.cml')) return;

  // Mark body for CSS scoping
  document.body.classList.add('cro-collection-page');

  // Detect paid traffic (UTM parameters)
  var params = new URLSearchParams(window.location.search);
  if (params.get('utm_source') || params.get('gclid') || params.get('fbclid') || params.get('ttclid')) {
    document.body.classList.add('cro-paid-traffic');
  }

  /* ─────────────────────────────────────────────────────────────────────
     UTILITY: Find a visible ancestor — walks up the DOM to find
     the first ancestor that has non-zero dimensions
     ───────────────────────────────────────────────────────────────────── */
  function findVisibleAncestor(el) {
    while (el && el !== document.body) {
      var rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return el;
      el = el.parentElement;
    }
    return null;
  }

  /* ─────────────────────────────────────────────────────────────────────
     DYNAMIC COLLECTION TITLE
     When a filter param (e.g. ?pf_opt_color=Love+of+Music+Ultra) is
     present, swap the hero H1 to match the filtered product name.
     Makes paid-traffic landing pages feel personalized.
     ───────────────────────────────────────────────────────────────────── */

  var _dynamicTitle = null;
  var _originalBaseName = null;

  function updateCollectionTitle() {
    // Only compute the title once
    if (_dynamicTitle) {
      applyDynamicTitle();
      return;
    }

    var params = new URLSearchParams(window.location.search);
    var colorFilter = params.get('pf_opt_color');
    var sizeFilter = params.get('pf_opt_size');
    if (!colorFilter && !sizeFilter) return;

    // Capture the original base name before we modify anything
    if (!_originalBaseName) {
      var existingH1 = document.querySelector('.boost-sd__header-title, .overlay-text__title');
      _originalBaseName = existingH1 ? existingH1.textContent.trim() : 'Limitless Straw Tumblers';
      // Remove "Series" for cleaner read
      _originalBaseName = _originalBaseName.replace(/\s+Series\s+/i, ' ');
    }

    // Color filter takes priority (more specific)
    if (colorFilter) {
      var name = colorFilter
        .replace(/\+/g, ' ')
        .replace(/\s+(Ultra|Alloy Ultra|Alloy)$/i, '')
        .trim();
      if (name) {
        _dynamicTitle = name + ' Tumbler Drop';
      }
    } else if (sizeFilter) {
      var size = sizeFilter.replace(/\+/g, ' ').trim().toUpperCase();
      _dynamicTitle = size + ' ' + _originalBaseName;
    }

    if (!_dynamicTitle) return;
    applyDynamicTitle();

    // Boost renders async — watch for the H1 to appear and override it
    if (!document.querySelector('._cro-title-observer')) {
      var marker = document.createElement('meta');
      marker.className = '_cro-title-observer';
      document.head.appendChild(marker);

      var titleObserver = new MutationObserver(function() {
        applyDynamicTitle();
      });
      titleObserver.observe(document.body, { childList: true, subtree: true });

      // Also poll for the first few seconds
      var polls = 0;
      var titlePoll = setInterval(function() {
        polls++;
        applyDynamicTitle();
        if (polls > 20) clearInterval(titlePoll);
      }, 500);
    }
  }

  function applyDynamicTitle() {
    if (!_dynamicTitle) return;

    // Update the Boost-rendered H1
    var boostTitle = document.querySelector('.boost-sd__header-title');
    if (boostTitle && boostTitle.textContent.trim() !== _dynamicTitle) {
      boostTitle.textContent = _dynamicTitle;
    }

    // Update the native Shopify H1
    var nativeTitle = document.querySelector('.overlay-text__title');
    if (nativeTitle && nativeTitle.textContent.trim() !== _dynamicTitle) {
      nativeTitle.textContent = _dynamicTitle;
    }

    // Update page <title>
    var expectedPageTitle = _dynamicTitle + ' | Coldest';
    if (document.title !== expectedPageTitle) {
      document.title = expectedPageTitle;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     PHASE 1: ABOVE-FOLD RESCUE
     ───────────────────────────────────────────────────────────────────── */

  function injectTrustBar() {
    if (document.querySelector('.cro-trust-bar')) return;

    var trustBar = document.createElement('div');
    trustBar.className = 'cro-trust-bar';
    trustBar.innerHTML = [
      '<div class="cro-trust-bar__item"><span class="cro-trust-bar__icon">&#9733;</span>Loved by Coldies</div>',
      '<div class="cro-trust-bar__item"><span class="cro-trust-bar__icon">&#127988;</span>Shark Tank Featured</div>',
      '<div class="cro-trust-bar__item"><span class="cro-trust-bar__icon">&#10052;</span>Cold Retention Up to 100+ Hours*</div>',
      '<div class="cro-trust-bar__item"><span class="cro-trust-bar__icon">&#128274;</span>Lids designed to seal tight</div>',
      '<div class="cro-trust-bar__item"><span class="cro-trust-bar__icon">&#128230;</span>Free Shipping $125+</div>'
    ].join('');

    // Strategy: Insert after the Boost Commerce collection header (hero),
    // which is the visible hero on pages where Boost replaces Shopify's rendering.
    var inserted = false;

    // Approach 1 (PRIMARY): Insert after Boost's collection header
    var boostHeader = document.querySelector('.boost-sd__collection-header');
    if (boostHeader) {
      var bhr = boostHeader.getBoundingClientRect();
      if (bhr.width > 0 && bhr.height > 0) {
        boostHeader.parentNode.insertBefore(trustBar, boostHeader.nextSibling);
        inserted = true;
      }
    }

    // Approach 2: Insert before Boost's filter/product list container
    if (!inserted) {
      var boostGrid = document.querySelector('.boost-sd__filter-product-list');
      if (boostGrid) {
        var bgr = boostGrid.getBoundingClientRect();
        if (bgr.width > 0) {
          boostGrid.parentNode.insertBefore(trustBar, boostGrid);
          inserted = true;
        }
      }
    }

    // Approach 3: Insert after a visible hero element (non-Boost pages)
    if (!inserted) {
      var hero = document.querySelector('.page-header--with-background');
      if (hero) {
        var visibleHero = findVisibleAncestor(hero);
        if (visibleHero) {
          var section = visibleHero.closest('.shopify-section') || visibleHero;
          var sRect = section.getBoundingClientRect();
          if (sRect.width > 0) {
            section.parentNode.insertBefore(trustBar, section.nextSibling);
            inserted = true;
          }
        }
      }
    }

    // Approach 4: Insert before the visible product grid
    if (!inserted) {
      var productBlocks = document.querySelectorAll('.product-block');
      if (productBlocks.length > 0) {
        var firstVisible = findVisibleAncestor(productBlocks[0]);
        if (firstVisible) {
          var gridParent = firstVisible;
          while (gridParent.parentElement && gridParent.parentElement !== document.body && gridParent.parentElement.tagName !== 'MAIN') {
            gridParent = gridParent.parentElement;
          }
          gridParent.parentNode.insertBefore(trustBar, gridParent);
          inserted = true;
        }
      }
    }
  }

  function injectHeroCTA() {
    // Removed — hero CTA no longer needed
  }

  function addGridAnchor() {
    // Add an anchor ID to the visible product grid
    var boostGrid = document.querySelector('.boost-sd__filter-product-list, #boost-pfs-filter-products, .boost-sd__product-list');
    if (boostGrid && !boostGrid.id) {
      boostGrid.id = 'cro-product-grid';
    } else {
      // Fallback to native grid
      var grid = document.querySelector('.product-list');
      if (grid && !grid.id) {
        grid.id = 'cro-product-grid';
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     PHASE 2: PRODUCT CARD ENHANCEMENT
     ───────────────────────────────────────────────────────────────────── */

  // Quick-Add Drawer — removed
  function setupQuickAddInterception() {}

  /* ─────────────────────────────────────────────────────────────────────
     PHASE 3: SPEED OPTIMIZATION (JS portion)
     ───────────────────────────────────────────────────────────────────── */

  function optimizeImages() {
    // Eagerly load first 4 product images (target both Boost and native grids)
    var productImages = document.querySelectorAll('.product-block, .boost-sd__product-item');
    for (var i = 0; i < Math.min(4, productImages.length); i++) {
      var imgs = productImages[i].querySelectorAll('img[loading="lazy"]');
      imgs.forEach(function(img) {
        img.setAttribute('loading', 'eager');
        if (i === 0) {
          img.setAttribute('fetchpriority', 'high');
        }
      });
    }
  }

  function deferNonCriticalScripts() {
    // Remove ReelUp container if present on collection pages
    var reelup = document.querySelector('.reelup-container, [data-reelup]');
    if (reelup) reelup.remove();
  }

  /* ─────────────────────────────────────────────────────────────────────
     PHASE 4: BELOW-FOLD CONVERSION ARCHITECTURE
     ───────────────────────────────────────────────────────────────────── */

  function injectMediaLogoBar() {
    if (document.querySelector('.cro-media-bar')) return;

    var mediaBar = document.createElement('div');
    mediaBar.className = 'cro-media-bar';
    mediaBar.innerHTML = '<div class="cro-media-bar__label">As Seen On</div>' +
      '<div class="cro-media-bar__logos">' +
      '<span class="cro-media-bar__logo">SHARK TANK</span>' +
      '<span class="cro-media-bar__logo">GOOD MORNING AMERICA</span>' +
      '<span class="cro-media-bar__logo">FORBES</span>' +
      '<span class="cro-media-bar__logo">BUSINESS INSIDER</span>' +
      '</div>';

    var inserted = false;

    // Strategy 1: Insert after Boost's filter-product-list container
    var boostGrid = document.querySelector('.boost-sd__filter-product-list');
    if (boostGrid) {
      var bgr = boostGrid.getBoundingClientRect();
      if (bgr.width > 0) {
        boostGrid.parentNode.insertBefore(mediaBar, boostGrid.nextSibling);
        inserted = true;
      }
    }

    // Strategy 2: Insert after legacy Boost container
    if (!inserted) {
      var boostProducts = document.querySelector('#boost-pfs-filter-products');
      if (boostProducts) {
        boostProducts.parentNode.insertBefore(mediaBar, boostProducts.nextSibling);
        inserted = true;
      }
    }

    // Strategy 3: Insert after any visible product list
    if (!inserted) {
      var productLists = document.querySelectorAll('.product-list');
      for (var i = 0; i < productLists.length; i++) {
        var r = productLists[i].getBoundingClientRect();
        if (r.width > 0) {
          productLists[i].parentNode.insertBefore(mediaBar, productLists[i].nextSibling);
          inserted = true;
          break;
        }
      }
    }

    // Strategy 4: Insert before the video section or FAQ
    if (!inserted) {
      var videoSection = document.querySelector('video, .shg-video, [data-section-type="video"]');
      if (videoSection) {
        var section = videoSection.closest('.shopify-section') || videoSection.parentElement;
        section.parentNode.insertBefore(mediaBar, section);
        inserted = true;
      }
    }
  }

  function injectComparisonTable() {
    if (document.querySelector('.cro-comparison')) return;

    var comparison = document.createElement('div');
    comparison.className = 'cro-comparison';
    var rows = [
      ['\u2744\uFE0F','Cold retention','Up to 100+ hours*'],
      ['\uD83D\uDD12','Lids','Designed to seal tight'],
      ['\uD83D\uDE97','Cupholder fit','Sized for standard holders'],
      ['\uD83D\uDEE1\uFE0F','Warranty','<a href="/pages/life">Limited lifetime</a>'],
      ['\uD83E\uDD64','Lids included','3 lids'],
      ['\uD83C\uDFA8','Designs','New colors most Thursdays']
    ];
    var rowsHtml = rows.map(function(r) {
      return '<tr><td class="cro-comparison__feat">' + r[0] + ' ' + r[1] + '</td>' +
        '<td class="cro-win">' + r[2] + '</td></tr>';
    }).join('');
    comparison.innerHTML = '<h3 class="cro-comparison__title">What you get with Coldest</h3>' +
      '<table>' +
      '<thead><tr><th>Feature</th><th>Coldest</th></tr></thead>' +
      '<tbody>' + rowsHtml + '</tbody>' +
      '</table>';

    // Insert after media bar if it exists
    var mediaBar = document.querySelector('.cro-media-bar');
    if (mediaBar && mediaBar.parentNode) {
      mediaBar.parentNode.insertBefore(comparison, mediaBar.nextSibling);
      return;
    }

    // Fallback: Insert after Boost product grid
    var boostGrid = document.querySelector('.boost-sd__filter-product-list, #boost-pfs-filter-products');
    if (boostGrid) {
      boostGrid.parentNode.insertBefore(comparison, boostGrid.nextSibling);
      return;
    }

    // Fallback 2: Insert before video/FAQ section
    var videoSection = document.querySelector('video, .shg-video');
    if (videoSection) {
      var section = videoSection.closest('.shopify-section') || videoSection.parentElement;
      section.parentNode.insertBefore(comparison, section);
    }
  }

  /* Curved-divider colour band (window.cldstBands from the layout <head>, palette slot 3 =
     the collection's coldest.band_colors[2], brand teal when empty): "As Seen On" + "Why Coldest
     Wins" share one full-bleed band with own-colour curved caps. Idempotent; no helper -> no-op. */
  function bandCroBlocks() {
    var CB = window.cldstBands;
    if (!CB || !CB.wrap) return;
    var mb = document.querySelector('.cro-media-bar');
    var cmp = document.querySelector('.cro-comparison');
    var nodes = [];
    if (mb && !mb.closest('.cldst-jsband')) nodes.push(mb);
    if (cmp && !cmp.closest('.cldst-jsband') && (!nodes.length || mb.nextElementSibling === cmp)) nodes.push(cmp);
    if (nodes.length) CB.wrap(nodes, 3, 'blob', 'curve', false, true, 'cldst-croband');
  }

  /* ─────────────────────────────────────────────────────────────────────
     PHASE 5: APP BLOAT REDUCTION (JS portion)
     ───────────────────────────────────────────────────────────────────── */

  function reduceAppBloat() {
    // Delay Tapcart app download bar on collection pages
    var tapcartBar = document.querySelector('.tc-mobile-download-banner-bottom');
    if (tapcartBar) {
      tapcartBar.style.display = 'none';
      // Show after 45 seconds for non-paid traffic
      if (!document.body.classList.contains('cro-paid-traffic')) {
        setTimeout(function() {
          tapcartBar.style.display = '';
        }, 45000);
      }
    }

    // Prevent WhatsApp chat widget from auto-opening
    var chatWidget = document.querySelector('.ctx-whatsapp-widget, [class*="whatsapp"]');
    if (chatWidget) {
      chatWidget.style.zIndex = '99';
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     INITIALIZATION
     ───────────────────────────────────────────────────────────────────── */

  function init() {
    try {
      // Dynamic title (before other injections)
      updateCollectionTitle();

      // Phase 1
      injectTrustBar();
      injectHeroCTA();
      addGridAnchor();

      // Phase 2
      setupQuickAddInterception();

      // Phase 3
      optimizeImages();
      deferNonCriticalScripts();

      // Phase 4
      injectMediaLogoBar();
      injectComparisonTable();
      bandCroBlocks();

      // Phase 5
      reduceAppBloat();
    } catch (e) {
      // Silent fail — don't break the page
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO]', e);
      }
    }
  }

  // Boost Commerce may render asynchronously — we need to wait for it.
  // Strategy: Run init() on DOMContentLoaded, then also watch for Boost
  // to finish rendering and re-run injection if needed.
  
  function initWhenReady() {
    init();

    if (!HAS_BOOST) return;

    // Also observe for Boost Commerce's async rendering
    // Boost replaces content dynamically, so we watch for its container
    var boostCheck = setInterval(function() {
      var boostContainer = document.querySelector('.boost-sd__filter-product-list, .boost-sd__collection-header, #boost-pfs-filter-products');
      if (boostContainer) {
        clearInterval(boostCheck);
        // Re-run injection functions that depend on the product grid
        setTimeout(function() {
          try {
            updateCollectionTitle();
            injectTrustBar();
            injectHeroCTA();
            injectMediaLogoBar();
            injectComparisonTable();
            addGridAnchor();
            setupQuickAddInterception();
            optimizeImages();
          } catch(e) {
            if (typeof console !== 'undefined' && console.error) {
              console.error('[CRO boost-ready]', e);
            }
          }
        }, 500);
      }
    }, 200);

    // Stop checking after 15 seconds
    setTimeout(function() { clearInterval(boostCheck); }, 15000);
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady);
  } else {
    initWhenReady();
  }

  // Also re-run after Boost AJAX filtering events
  var observer = new MutationObserver(function(mutations) {
    var shouldReinject = false;
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length > 3) {
        shouldReinject = true;
      }
    });
    if (shouldReinject) {
      setTimeout(function() {
        try {
          optimizeImages();
          // Re-inject if elements were removed during Boost re-render
          if (!document.querySelector('.cro-trust-bar')) injectTrustBar();
          if (!document.querySelector('.cro-hero-cta')) injectHeroCTA();
          if (!document.querySelector('.cro-media-bar')) injectMediaLogoBar();
          if (!document.querySelector('.cro-comparison')) injectComparisonTable();
          bandCroBlocks();
        } catch(e) {}
      }, 300);
    }
  });

  // Observe the Boost container for changes (filtering, pagination)
  var boostTarget = document.querySelector('.boost-sd__filter-product-list, #boost-pfs-filter-products, .boost-sd__app');
  if (boostTarget) {
    observer.observe(boostTarget, { childList: true, subtree: true });
  } else if (HAS_BOOST) {
    // Wait for Boost to appear, then observe
    var waitForBoost = setInterval(function() {
      var bt = document.querySelector('.boost-sd__filter-product-list, #boost-pfs-filter-products, .boost-sd__app');
      if (bt) {
        clearInterval(waitForBoost);
        observer.observe(bt, { childList: true, subtree: true });
      }
    }, 500);
    setTimeout(function() { clearInterval(waitForBoost); }, 10000);
  }

  // ─── Collection Card Drop Badges ──────────────────────────────
  // Tue 12:01am – Wed 9:00pm ET  →  "SNEAK PEEK" on first 2 cards
  // Thu 12:00am – Mon 9:00pm ET  →  "JUST DROPPED!" on first 2 cards
  // Badge appears top-right of card image (boost-sd__product-image-column--right)
  (function() {
    if (!HAS_BOOST) return; // badges only .boost-sd__product-item cards
    var DROP_COUNT = 2; // number of newest items to badge

    function getDropBadge() {
      var now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      var day = now.getDay(); // 0=Sun..6=Sat
      var hour = now.getHours();

      // Tue(2) or Wed(3) before 9pm → Sneak Peek
      if (day === 2 || (day === 3 && hour < 21)) {
        return { text: 'SNEAK PEEK', cls: 'cro-drop-badge--sneak' };
      }
      // Wed 9pm+ → Just Dropped (reveal night)
      if (day === 3 && hour >= 21) {
        return { text: 'JUST DROPPED!', cls: 'cro-drop-badge--dropped' };
      }
      // Thu(4), Fri(5), Sat(6), Sun(0) → Just Dropped
      if (day === 4 || day === 5 || day === 6 || day === 0) {
        return { text: 'JUST DROPPED!', cls: 'cro-drop-badge--dropped' };
      }
      // Mon(1) before 9pm → Just Dropped
      if (day === 1 && hour < 21) {
        return { text: 'JUST DROPPED!', cls: 'cro-drop-badge--dropped' };
      }
      // Mon 9pm+ → no badge (gap before Tue)
      return null;
    }

    function addDropBadges() {
      var badge = getDropBadge();
      if (!badge) return;

      var cards = document.querySelectorAll('.boost-sd__product-item');
      for (var i = 0; i < Math.min(DROP_COUNT, cards.length); i++) {
        var card = cards[i];
        if (card.querySelector('.cro-drop-badge')) continue; // already badged

        // Find the right column in the image overlay row
        var rightCol = card.querySelector('.boost-sd__product-image-column--right');
        if (rightCol) {
          var el = document.createElement('div');
          el.className = 'cro-drop-badge ' + badge.cls;
          el.textContent = badge.text;
          rightCol.appendChild(el);
          /* If this card already has an inventory badge, restack it */
          var invB = card.querySelector('.coldest-inv-badge');
          if (invB && !invB.classList.contains('coldest-inv-badge--stacked')) {
            invB.classList.add('coldest-inv-badge--stacked');
          }
        }
      }
    }

    // Run after Boost renders + re-run on mutations
    setTimeout(addDropBadges, 1500);
    setTimeout(addDropBadges, 3000);
    setTimeout(addDropBadges, 5000);

    var badgeObs = new MutationObserver(function() { addDropBadges(); });
    var waitBadge = setInterval(function() {
      var grid = document.querySelector('.boost-sd__product-list');
      if (grid) {
        clearInterval(waitBadge);
        badgeObs.observe(grid, { childList: true, subtree: true });
      }
    }, 500);
    setTimeout(function() { clearInterval(waitBadge); }, 10000);
  })();

  // ── Inventory badges ("Few Remaining" / "N Remaining") on Boost cards ──
  (function inventoryBadgesIIFE() {
    if (!HAS_BOOST) return; // badges only .boost-sd__product-item cards
    // Inject one-time CSS (orange badge in top-right, above existing Boost badges)
    if (!document.getElementById('coldest-inv-badge-css')) {
      var style = document.createElement('style');
      style.id = 'coldest-inv-badge-css';
      style.textContent = [
        /* Our inventory badge - match discount badge margin/shape */
        '.coldest-inv-badge{position:absolute;top:6px;right:16px;background:#ff6f00;color:#fff;',
        'font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;',
        'padding:5px 9px;border-radius:5px;z-index:5;pointer-events:none;white-space:nowrap;',
        'font-family:inherit;border:none;box-shadow:none}',
        /* When stacked under a drop badge, shift inventory badge down */
        '.coldest-inv-badge--stacked{top:46px}',
        '.boost-sd__product-item{position:relative}',
        /* Normalize Boost discount/sale pill to match our rectangle shape (no shadow/outline) */
        '.boost-sd__product-item .boost-sd__product-label--text,',
        '.boost-sd__product-item .boost-sd__product-label--rectangle,',
        '.boost-sd__product-item .boost-sd__product-label-text{',
        'border-radius:5px !important;padding:5px 9px !important;font-size:11px !important;',
        'font-weight:700 !important;text-transform:uppercase !important;letter-spacing:.5px !important;',
        'line-height:1.3 !important;height:auto !important;width:auto !important;',
        'box-shadow:none !important;border:none !important;outline:none !important;',
        'text-shadow:none !important;background-clip:padding-box !important}',
        /* Normalize JUST DROPPED / SNEAK PEEK badge to match rectangle shape + positioning */
        '.cro-drop-badge{border-radius:5px !important;padding:5px 9px !important;font-size:11px !important;',
        'font-weight:700 !important;text-transform:uppercase !important;letter-spacing:.5px !important;',
        'line-height:1.3 !important;box-shadow:none !important;border:none !important;',
        'outline:none !important;text-shadow:none !important;position:absolute !important;',
        'top:6px !important;right:16px !important;z-index:4 !important;white-space:nowrap}',
        '@media(max-width:600px){.coldest-inv-badge{font-size:10px;padding:4px 7px;top:4px;right:12px}',
        '.coldest-inv-badge--stacked{top:36px}',
        '.boost-sd__product-item .boost-sd__product-label--text,',
        '.boost-sd__product-item .boost-sd__product-label--rectangle,',
        '.boost-sd__product-item .boost-sd__product-label-text,',
        '.cro-drop-badge{font-size:10px !important;padding:4px 7px !important}',
        '.cro-drop-badge{top:4px !important;right:12px !important}}'
      ].join('');
      document.head.appendChild(style);
    }

    function getInventory() {
      // Request 201: a snapshot older than 3 days shows no badge, not an old count.
      var at = Date.parse(window.__coldestInventoryAt || '');
      if (isNaN(at) || Date.now() - at > 3 * 864e5) return null;
      return window.__coldestInventory || null;
    }

    function variantIdFromCard(card) {
      // Boost cards expose the variant ID on data-product-id (confusingly named, it's variant)
      var vid = card.getAttribute('data-product-id');
      if (vid && vid.length > 0 && !isNaN(Number(vid))) return vid;
      // Fallback: parse from data-product JSON attribute
      var raw = card.getAttribute('data-product');
      if (raw) {
        try {
          var decoded = raw.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
          var parsed = JSON.parse(decoded);
          if (parsed && parsed.variantId) return String(parsed.variantId);
        } catch (e) {}
      }
      return null;
    }

    function makeBadgeText(qty) {
      if (qty < 90) return qty + ' Remaining';
      if (qty < 200) return 'Few Remaining';
      return null;
    }

    function applyInventoryBadges() {
      var inv = getInventory();
      if (!inv) return;
      var cards = document.querySelectorAll('.boost-sd__product-item');
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        if (card.querySelector('.coldest-inv-badge')) continue; // already badged
        var vid = variantIdFromCard(card);
        if (!vid) continue;
        var qty = inv[vid];
        if (typeof qty !== 'number' || qty <= 0) continue;
        var text = makeBadgeText(qty);
        if (!text) continue;
        var b = document.createElement('span');
        var hasDropSibling = !!card.querySelector('.cro-drop-badge');
        b.className = 'coldest-inv-badge' + (hasDropSibling ? ' coldest-inv-badge--stacked' : '');
        b.textContent = text;
        card.appendChild(b);
      }
    }

    function start() {
      // Retry passes (Boost renders cards over ~1-3s, plus on filter/pagination changes)
      setTimeout(applyInventoryBadges, 1500);
      setTimeout(applyInventoryBadges, 3000);
      setTimeout(applyInventoryBadges, 5000);

      // Observe grid for dynamic changes (filter, sort, load more)
      var obs = new MutationObserver(function () { applyInventoryBadges(); });
      var tries = 0;
      var waiter = setInterval(function () {
        tries++;
        var grid = document.querySelector('.boost-sd__product-list');
        if (grid) {
          clearInterval(waiter);
          obs.observe(grid, { childList: true, subtree: true });
        } else if (tries > 40) {
          clearInterval(waiter);
        }
      }, 500);
    }

    // Also wait for inventory data to load if it hasn't yet
    if (getInventory()) {
      start();
    } else {
      var invTries = 0;
      var invWaiter = setInterval(function () {
        invTries++;
        if (getInventory()) {
          clearInterval(invWaiter);
          start();
        } else if (invTries > 40) {
          clearInterval(invWaiter);
        }
      }, 250);
    }
  })();

  // ── Quick-add bag overlay on Boost product cards ──────────────
  // Ports the drop-framework quick-add (.drop-card-bag) to the NATIVE,
  // Boost-rendered collections (e.g. /collections/limitless-bottle-with-handle):
  // a circular bag-icon button bottom-right of each card image; click →
  // /cart/add.js with a spinner → green check → opens the cart drawer, then
  // reverts. Mobile: always visible. Desktop: hover-reveal on the card.
  (function quickAddBagIIFE() {
    if (!HAS_BOOST) return; // bag buttons only on .boost-sd__product-item cards
    var BAG_ICON = '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></svg>';
    var BAG_SPIN = '<span class="coldest-bag__spin"></span>';
    var BAG_CHECK = '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>';

    if (!document.getElementById('coldest-bag-overlay-css')) {
      var style = document.createElement('style');
      style.id = 'coldest-bag-overlay-css';
      style.textContent = [
        '.coldest-bag{position:absolute;bottom:10px;right:10px;z-index:4;width:42px;height:42px;border-radius:50%;border:none;background:#111;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;box-shadow:0 3px 12px rgba(0,0,0,.28);-webkit-tap-highlight-color:transparent;transition:transform .16s ease,background .16s ease,opacity .16s ease}',
        '.coldest-bag svg{display:block}',
        '.coldest-bag:active{transform:scale(.92)}',
        '.coldest-bag--adding{background:#444}',
        '.coldest-bag--added{background:#27ae60}',
        '.coldest-bag__spin{width:16px;height:16px;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:coldestbagspin .6s linear infinite}',
        '@keyframes coldestbagspin{to{transform:rotate(360deg)}}',
        /* Desktop (hover-capable): hidden until the card is hovered */
        '@media (hover:hover) and (min-width:769px){.coldest-bag{opacity:0;transform:translateY(8px)}.boost-sd__product-item:hover .coldest-bag,.coldest-bag:focus-visible{opacity:1;transform:translateY(0)}.boost-sd__product-item:hover .coldest-bag:hover{transform:scale(1.08);background:#000}}',
        /* Mobile: persistent, slightly smaller */
        '@media (max-width:768px){.coldest-bag{width:38px;height:38px;bottom:8px;right:8px;box-shadow:0 2px 8px rgba(0,0,0,.3)}}',
        '@media (max-width:480px){.coldest-bag{width:35px;height:35px;bottom:7px;right:7px}.coldest-bag svg{width:17px;height:17px}}'
      ].join('');
      document.head.appendChild(style);
    }

    function variantIdFromCard(card) {
      // Boost exposes the variant ID on data-product-id (same as the inventory
      // badges above rely on); fall back to the data-product JSON blob.
      var vid = card.getAttribute('data-product-id');
      if (vid && vid.length > 0 && !isNaN(Number(vid))) return vid;
      var raw = card.getAttribute('data-product');
      if (raw) {
        try {
          var parsed = JSON.parse(raw.replace(/&quot;/g, '"').replace(/&amp;/g, '&'));
          if (parsed && parsed.variantId) return String(parsed.variantId);
        } catch (e) {}
      }
      return null;
    }

    function isSoldOut(card) {
      if (card.querySelector('.boost-sd__product-status--sold-out, .boost-sd__product-label--soldout, .boost-sd__product-label--sold-out')) return true;
      return /sold\s*out/i.test(card.textContent || '');
    }

    function imageContainer(card) {
      // Anchor the bag over the IMAGE only (not the whole card, which also
      // contains title/price below). Prefer Boost's image wrapper; fall back
      // to the overlay column's ancestor, then the product link.
      var wrap = card.querySelector('.boost-sd__product-image-wrapper') ||
                 card.querySelector('.boost-sd__product-image-inner') ||
                 card.querySelector('.boost-sd__product-image');
      if (wrap) return wrap;
      var rightCol = card.querySelector('.boost-sd__product-image-column--right');
      if (rightCol && rightCol.parentElement && rightCol.parentElement.parentElement) {
        return rightCol.parentElement.parentElement;
      }
      return card.querySelector('.boost-sd__product-link') || null;
    }

    function bindBag(btn) {
      if (btn.__bagBound) return; btn.__bagBound = true;
      btn.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        if (btn.classList.contains('coldest-bag--adding') || btn.classList.contains('coldest-bag--added')) return;
        var vid = btn.getAttribute('data-variant-id');
        btn.classList.add('coldest-bag--adding');
        btn.innerHTML = BAG_SPIN;
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
        })
        .then(function(r) { return r.json(); })
        .then(function() {
          btn.classList.remove('coldest-bag--adding');
          btn.classList.add('coldest-bag--added');
          btn.innerHTML = BAG_CHECK;
          if (typeof window.coldestOpenCartDrawer === 'function') window.coldestOpenCartDrawer(); else if (typeof window.MonsterCart !== 'undefined') { try { window.MonsterCart.open(); } catch (ex) {} }
          document.dispatchEvent(new CustomEvent('cart:updated'));
          setTimeout(function() { btn.classList.remove('coldest-bag--added'); btn.innerHTML = BAG_ICON; }, 1800);
        })
        .catch(function() {
          btn.classList.remove('coldest-bag--adding');
          btn.innerHTML = BAG_ICON;
        });
      });
    }

    function addBagButtons() {
      var cards = document.querySelectorAll('.boost-sd__product-item');
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        if (card.querySelector('.coldest-bag')) continue; // already has one
        if (isSoldOut(card)) continue;                    // OOS: no quick-add
        var vid = variantIdFromCard(card);
        if (!vid) continue;
        var wrap = imageContainer(card);
        if (!wrap) continue;
        var cs = window.getComputedStyle(wrap);
        if (cs && cs.position === 'static') wrap.style.position = 'relative';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'coldest-bag';
        btn.setAttribute('data-variant-id', vid);
        btn.setAttribute('aria-label', 'Add to bag');
        btn.setAttribute('title', 'Add to bag');
        btn.innerHTML = BAG_ICON;
        bindBag(btn);
        wrap.appendChild(btn);
      }
    }

    // Boost renders cards async (~1-3s) and re-renders on filter/sort/paginate.
    setTimeout(addBagButtons, 1500);
    setTimeout(addBagButtons, 3000);
    setTimeout(addBagButtons, 5000);
    var obs = new MutationObserver(function() { addBagButtons(); });
    var tries = 0;
    var waiter = setInterval(function() {
      tries++;
      var grid = document.querySelector('.boost-sd__product-list');
      if (grid) {
        clearInterval(waiter);
        obs.observe(grid, { childList: true, subtree: true });
        addBagButtons();
      } else if (tries > 40) {
        clearInterval(waiter);
      }
    }, 500);
  })();

})();
